
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { 
  Currency, 
  Expense, 
  CategoryBudget, 
  Investment, 
  InvestmentCategory,
  BalanceSheetItem, 
  CashFlowDetail, 
  CashFlowEvent,
  JanuaryBaseline,
  FinanceContextType,
  HistoryPoint,
  FinancialFreedomStats,
  CreditCard,
  CreditCardPaymentRecord,
  CashLedgerEntry
} from '../types';
import { useAuth, supabase } from './AuthContext';

const FinanceContext = createContext<FinanceContextType | undefined>(undefined);

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isValidUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export const FinanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  const [currency, setCurrency] = useState<Currency>(Currency.USD);
  const [exchangeRates, setExchangeRates] = useState<Record<Currency, number>>({
    [Currency.USD]: 1.0,
    [Currency.EUR]: 0.92,
    [Currency.AED]: 3.67
  });

  const [isDarkMode, setIsDarkModeState] = useState<boolean>(() => {
    const saved = localStorage.getItem('themeMode');
    return saved ? saved === 'dark' : false; // default to white/light mode
  });

  const setIsDarkMode = (dark: boolean) => {
    setIsDarkModeState(dark);
    localStorage.setItem('themeMode', dark ? 'dark' : 'light');
  };

  const getInitialCashForYear = (yr: number) => {
    try {
      const saved = localStorage.getItem(`fi_available_cash_${yr}`);
      if (saved !== null && saved !== '') {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed)) return parsed;
      }
    } catch (_) {}
    if (yr === new Date().getFullYear()) {
      try {
        const legacy = localStorage.getItem('fi_available_cash');
        if (legacy !== null && legacy !== '') {
          const parsed = parseFloat(legacy);
          if (!isNaN(parsed)) return parsed;
        }
      } catch (_) {}
      return 15000;
    }
    return 0;
  };

  // Year-over-Year Global State
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    const saved = localStorage.getItem('fi_selected_year');
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 2020 && parsed <= 2040) return parsed;
    }
    return new Date().getFullYear();
  });

  const setSelectedYear = (yr: number) => {
    setSelectedYearState(yr);
    try {
      localStorage.setItem('fi_selected_year', yr.toString());
    } catch (e) {
      console.error(e);
    }
    const cashForYr = getInitialCashForYear(yr);
    setAvailableCashState(cashForYr);
  };

  const availableYears = useMemo(() => {
    const current = new Date().getFullYear();
    const base = [current - 2, current - 1, current, current + 1, current + 2, current + 3, current + 4];
    if (!base.includes(selectedYear)) {
      base.push(selectedYear);
      base.sort((a, b) => a - b);
    }
    return base;
  }, [selectedYear]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [isDarkMode]);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [budgets, setBudgets] = useState<CategoryBudget[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [investmentCategories, setInvestmentCategories] = useState<InvestmentCategory[]>([]);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetItem[]>([]);
  const [cashFlows, setCashFlows] = useState<CashFlowDetail[]>([]);
  const [cashFlowEvents, setCashFlowEvents] = useState<CashFlowEvent[]>(() => {
    try {
      const saved = localStorage.getItem('fi_cash_flow_events');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    const currentYear = new Date().getFullYear();
    return [
      {
        id: 'cfe-salary',
        description: 'Primary Professional Retainer / Salary',
        amount: 24000,
        type: 'Inflow',
        frequency: 'Monthly',
        startDate: `${currentYear}-01-01`,
        endDate: undefined
      },
      {
        id: 'cfe-dividends',
        description: 'Investment Dividends & Yield',
        amount: 3500,
        type: 'Inflow',
        frequency: 'Monthly',
        startDate: `${currentYear}-01-01`,
        endDate: undefined
      },
      {
        id: 'cfe-living',
        description: 'Fixed Living Costs & Housing',
        amount: 9500,
        type: 'Outflow',
        frequency: 'Monthly',
        startDate: `${currentYear}-01-01`,
        endDate: undefined
      },
      {
        id: 'cfe-operational',
        description: 'Operational & Discretionary Forecast',
        amount: 3500,
        type: 'Outflow',
        frequency: 'Monthly',
        startDate: `${currentYear}-01-01`,
        endDate: undefined
      }
    ];
  });
  const [janBaseline, setJanBaseline] = useState<JanuaryBaseline | null>(null);

  const [passiveIncomeGoal, setPassiveIncomeGoalState] = useState<number>(() => {
    const saved = localStorage.getItem('fi_passive_income_goal');
    return saved ? parseFloat(saved) || 3000 : 3000;
  });

  const setPassiveIncomeGoal = (val: number) => {
    setPassiveIncomeGoalState(val);
    localStorage.setItem('fi_passive_income_goal', val.toString());
  };

  // Lifestyle Monthly Expenses (Defaults to budgeted monthly expenses if null)
  const [lifestyleMonthlyExpenses, setLifestyleMonthlyExpensesState] = useState<number | null>(() => {
    const saved = localStorage.getItem('fi_lifestyle_monthly_expenses');
    return saved !== null && saved !== '' ? (parseFloat(saved) || null) : null;
  });

  const setLifestyleMonthlyExpenses = (amt: number | null) => {
    setLifestyleMonthlyExpensesState(amt);
    if (amt === null) {
      localStorage.removeItem('fi_lifestyle_monthly_expenses');
    } else {
      localStorage.setItem('fi_lifestyle_monthly_expenses', amt.toString());
    }
  };

  // Credit Cards & Current Liabilities State
  const [creditCards, setCreditCards] = useState<CreditCard[]>(() => {
    try {
      const savedUser = localStorage.getItem('fi_credit_cards_active');
      const saved = savedUser || localStorage.getItem('fi_credit_cards');
      if (saved !== null && saved !== '') {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    return [];
  });

  const saveCreditCards = async (cards: CreditCard[]) => {
    setCreditCards(cards);
    const activeUserId = user?.id || 'default';
    try {
      localStorage.setItem('fi_credit_cards', JSON.stringify(cards));
      localStorage.setItem('fi_credit_cards_active', JSON.stringify(cards));
      if (user?.id) {
        localStorage.setItem(`fi_credit_cards_${user.id}`, JSON.stringify(cards));
      }
    } catch (e) {
      console.error(e);
    }

    // 1. Backend Database API persistence (scoped by user ID)
    try {
      await fetch('/api/credit-cards/sync', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-user-id': activeUserId
        },
        body: JSON.stringify({ 
          userId: activeUserId, 
          creditCards: cards 
        })
      });
    } catch (e) {
      console.warn('[CreditCard DB] sync failed:', e);
    }

    // 2. Supabase DB persistence for user
    if (user && supabase) {
      try {
        // A. Upsert remaining cards into Supabase balance_sheet table so cards are stored directly as liabilities
        for (const c of cards) {
          const cardUuid = isValidUUID(c.id) ? c.id : generateUUID();
          await supabase.from('balance_sheet').upsert({
            id: cardUuid,
            user_id: user.id,
            type: 'Liability',
            name: `[Credit Card] ${c.name}`,
            value: c.amountToPay,
            monthly_payment: c.minimumPayment || 0,
            duration_months: c.creditLimit ? Math.round(c.creditLimit) : 0,
            remaining_months: c.apr ? Math.round(c.apr) : 0,
            is_loan: false,
            date: c.deadline || new Date().toISOString().split('T')[0],
            start_date: c.deadline || new Date().toISOString().split('T')[0],
            end_date: c.isPaid ? 'PAID' : (c.issuer || 'UNPAID')
          }, { onConflict: 'id' });
        }

        // B. Clean up any [Credit Card] items in balance_sheet that are no longer in `cards`
        const { data: existingCcRows } = await supabase
          .from('balance_sheet')
          .select('id, name')
          .eq('user_id', user.id)
          .ilike('name', '[Credit Card]%');

        if (existingCcRows && existingCcRows.length > 0) {
          const activeIds = new Set(cards.map(c => c.id));
          const activeNames = new Set(cards.map(c => `[Credit Card] ${c.name}`));
          const obsoleteRows = existingCcRows.filter(r => !activeIds.has(r.id) && !activeNames.has(r.name));
          for (const obs of obsoleteRows) {
            await supabase.from('balance_sheet').delete().eq('id', obs.id).eq('user_id', user.id);
          }
        }

        // C. Also preserve rich metadata in profiles.jan_baseline
        const { data: prof } = await supabase.from('profiles').select('jan_baseline').eq('id', user.id).maybeSingle();
        const currentBase = prof?.jan_baseline || {};
        await supabase.from('profiles').upsert({
          id: user.id,
          jan_baseline: { ...currentBase, creditCards: cards }
        });
      } catch (dbErr) {
        console.warn('[CreditCard DB] Supabase save failed:', dbErr);
      }
    }
  };

  const addCreditCard = (card: Omit<CreditCard, 'id'> | CreditCard) => {
    const rawId = ('id' in card && card.id) ? card.id : '';
    const newCard: CreditCard = {
      ...card,
      id: isValidUUID(rawId) ? rawId : generateUUID(),
      isPaid: card.isPaid ?? false
    };
    const updated = [newCard, ...creditCards];
    saveCreditCards(updated);
  };

  const updateCreditCard = (card: CreditCard) => {
    const updated = creditCards.map(c => c.id === card.id ? card : c);
    saveCreditCards(updated);
  };

  const deleteCreditCard = (id: string) => {
    const cardToDelete = creditCards.find(c => c.id === id);
    const updated = creditCards.filter(c => c.id !== id);
    setCreditCards(updated);
    saveCreditCards(updated);

    const activeUserId = user?.id || 'default';
    // Explicitly delete from server DB
    fetch(`/api/credit-cards/${encodeURIComponent(id)}?userId=${encodeURIComponent(activeUserId)}`, {
      method: 'DELETE',
      headers: {
        'x-user-id': activeUserId
      }
    }).catch(() => {});

    // Explicitly delete from Supabase balance_sheet table if user is connected
    if (user && supabase) {
      if (isValidUUID(id)) {
        supabase.from('balance_sheet').delete().eq('id', id).eq('user_id', user.id).then(() => {}).catch(() => {});
      }
      if (cardToDelete?.name) {
        supabase.from('balance_sheet').delete().eq('name', `[Credit Card] ${cardToDelete.name}`).eq('user_id', user.id).then(() => {}).catch(() => {});
      }
    }
  };

  // Credit Card Payment History Ledger
  const [creditCardLedger, setCreditCardLedger] = useState<CreditCardPaymentRecord[]>(() => {
    try {
      const saved = localStorage.getItem('fi_credit_card_ledger');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    const currentYear = new Date().getFullYear();
    // Starter historical records for previous months
    return [
      {
        id: 'ccl-1',
        cardId: 'cc-start-1',
        cardName: 'Amex Platinum',
        issuer: 'American Express',
        last4: '1004',
        amountPaid: 2150,
        deadline: `${currentYear}-01-20`,
        monthKey: `${currentYear}-01`,
        monthIndex: 0,
        year: currentYear,
        paidAt: `${currentYear}-01-19T14:30:00.000Z`,
        timestampFormatted: `Jan 19, ${currentYear}, 2:30 PM`,
        note: 'Settled via auto-pay checking'
      },
      {
        id: 'ccl-2',
        cardId: 'cc-start-2',
        cardName: 'Chase Sapphire Preferred',
        issuer: 'Chase',
        last4: '4892',
        amountPaid: 1420,
        deadline: `${currentYear}-02-15`,
        monthKey: `${currentYear}-02`,
        monthIndex: 1,
        year: currentYear,
        paidAt: `${currentYear}-02-14T10:15:00.000Z`,
        timestampFormatted: `Feb 14, ${currentYear}, 10:15 AM`,
        note: 'Full statement balance cleared'
      },
      {
        id: 'ccl-3',
        cardId: 'cc-start-3',
        cardName: 'Apple Card',
        issuer: 'Goldman Sachs',
        last4: '7731',
        amountPaid: 840,
        deadline: `${currentYear}-03-31`,
        monthKey: `${currentYear}-03`,
        monthIndex: 2,
        year: currentYear,
        paidAt: `${currentYear}-03-30T19:00:00.000Z`,
        timestampFormatted: `Mar 30, ${currentYear}, 7:00 PM`,
        note: 'Apple Pay monthly statement'
      }
    ];
  });

  const saveCreditCardLedger = async (ledger: CreditCardPaymentRecord[]) => {
    setCreditCardLedger(ledger);
    try {
      localStorage.setItem('fi_credit_card_ledger', JSON.stringify(ledger));
      if (user?.id) {
        localStorage.setItem(`fi_credit_card_ledger_${user.id}`, JSON.stringify(ledger));
      }
    } catch (e) {
      console.error(e);
    }
    // 1. Database API persistence
    try {
      await fetch('/api/credit-cards/sync', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-user-id': user?.id || 'default'
        },
        body: JSON.stringify({ 
          userId: user?.id || 'default',
          creditCardLedger: ledger 
        })
      });
    } catch (e) {
      console.warn('[CreditCard DB] ledger sync failed:', e);
    }
    // 2. Supabase user database persistence
    if (user && supabase) {
      try {
        const { data: prof } = await supabase.from('profiles').select('jan_baseline').eq('id', user.id).maybeSingle();
        const currentBase = prof?.jan_baseline || {};
        await supabase.from('profiles').upsert({
          id: user.id,
          jan_baseline: { ...currentBase, creditCardLedger: ledger }
        });
      } catch (dbErr) {
        console.warn('[CreditCard DB] Supabase ledger save failed:', dbErr);
      }
    }
  };

  const recordCreditCardPayment = (card: CreditCard, amountPaid?: number, note?: string) => {
    const d = new Date();
    const parts = card.deadline ? card.deadline.split('-') : [];
    const yr = parts.length >= 2 ? parseInt(parts[0], 10) : d.getFullYear();
    const mNum = parts.length >= 2 ? parseInt(parts[1], 10) - 1 : d.getMonth();
    const amt = amountPaid !== undefined ? amountPaid : card.amountToPay;

    const newRecord: CreditCardPaymentRecord = {
      id: generateUUID(),
      cardId: card.id,
      cardName: card.name,
      issuer: card.issuer,
      last4: card.last4,
      amountPaid: amt,
      deadline: card.deadline,
      monthKey: `${yr}-${String(mNum + 1).padStart(2, '0')}`,
      monthIndex: mNum,
      year: yr,
      paidAt: d.toISOString(),
      timestampFormatted: d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
      note: note || `Paid statement of ${card.name}`
    };

    const updated = [newRecord, ...creditCardLedger];
    saveCreditCardLedger(updated);
  };

  const deleteCreditCardPaymentRecord = (id: string) => {
    const updated = creditCardLedger.filter(r => r.id !== id);
    saveCreditCardLedger(updated);
  };

  const toggleCreditCardPaid = (id: string) => {
    const card = creditCards.find(c => c.id === id);
    const willBePaid = card ? !card.isPaid : false;
    
    if (card && willBePaid) {
      // Record in Credit Card Ledger
      recordCreditCardPayment(card, card.amountToPay, `Statement marked paid via card tracker`);
    }

    const updated = creditCards.map(c => c.id === id ? { ...c, isPaid: !c.isPaid } : c);
    saveCreditCards(updated);
  };

  // Available Cash (Starting Liquid Liquidity for Balance Calculations)
  const [availableCash, setAvailableCashState] = useState<number>(() => {
    const savedYear = (() => {
      try {
        const y = localStorage.getItem('fi_selected_year');
        if (y) {
          const parsedY = parseInt(y, 10);
          if (!isNaN(parsedY)) return parsedY;
        }
      } catch (_) {}
      return new Date().getFullYear();
    })();
    return getInitialCashForYear(savedYear);
  });

  const setAvailableCash = (val: number) => {
    setAvailableCashState(val);
    try {
      localStorage.setItem(`fi_available_cash_${selectedYear}`, val.toString());
      if (selectedYear === new Date().getFullYear()) {
        localStorage.setItem('fi_available_cash', val.toString());
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Cash Ledger History (Keeps track of available cash updates with timestamps and month attribution)
  const [cashLedgerHistory, setCashLedgerHistory] = useState<CashLedgerEntry[]>(() => {
    try {
      const saved = localStorage.getItem('fi_cash_ledger_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Starter baseline monthly records for the year (Jan - Dec)
    const starters: CashLedgerEntry[] = [];
    const baseAmounts = [12000, 12800, 13500, 14000, 14200, 14800, 15000, 15300, 15500, 15000, 15200, 15400];
    
    for (let m = 0; m < 12; m++) {
      const isCurr = m === currentMonth;
      const d = new Date(currentYear, m, isCurr ? new Date().getDate() : 28, 12, 0, 0);
      starters.push({
        id: `cash-entry-${m}`,
        monthKey: `${currentYear}-${String(m + 1).padStart(2, '0')}`,
        monthIndex: m,
        year: currentYear,
        monthName: `${months[m]} ${currentYear}`,
        amount: isCurr ? 15000 : baseAmounts[m],
        timestamp: d.toISOString(),
        timestampFormatted: d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
        source: 'Sync Ledger',
        note: isCurr ? 'Active monthly available cash ledger' : `Reconciled cash for ${months[m]}`
      });
    }
    return starters.reverse();
  });

  const saveCashLedgerHistory = (entries: CashLedgerEntry[]) => {
    setCashLedgerHistory(entries);
    try {
      localStorage.setItem('fi_cash_ledger_history', JSON.stringify(entries));
    } catch (e) {
      console.error(e);
    }
  };

  // Sync Ledger Functionality:
  // Registers timestamp of when cash ledger is updated, updates account ledger cash amount of current,
  // and keeps track of the previous on the history of the ledger.
  const syncCashLedger = (amount?: number, monthIndex?: number, year?: number, note?: string) => {
    const targetAmt = amount !== undefined ? amount : availableCash;
    const now = new Date();
    const targetYear = year !== undefined ? year : selectedYear;
    const targetMonth = monthIndex !== undefined ? monthIndex : (targetYear === now.getFullYear() ? now.getMonth() : 0);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthKey = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}`;
    const monthName = `${months[targetMonth]} ${targetYear}`;

    // Find previous historical entry for this month
    const existingForMonth = cashLedgerHistory.find(e => e.monthKey === monthKey || (e.monthIndex === targetMonth && e.year === targetYear));
    const previousAmount = existingForMonth ? existingForMonth.amount : undefined;

    const newEntry: CashLedgerEntry = {
      id: generateUUID(),
      monthKey,
      monthIndex: targetMonth,
      year: targetYear,
      monthName,
      amount: targetAmt,
      timestamp: now.toISOString(),
      timestampFormatted: now.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
      source: 'Sync Ledger',
      note: note || (previousAmount !== undefined 
        ? `Reconciled from $${previousAmount.toLocaleString()} to $${targetAmt.toLocaleString()}` 
        : `Initial cash ledger sync`)
    };

    // Prepend to history (keeps all previous historical entries intact)
    const updatedHistory = [newEntry, ...cashLedgerHistory];
    saveCashLedgerHistory(updatedHistory);

    // Update current available cash state
    setAvailableCash(targetAmt);

    // Update account ledger cash amount of current in balanceSheet items
    const cashItemName = 'Liquid Cash Reserves';
    const existingBsIndex = balanceSheet.findIndex(item => 
      item.type === 'Asset' && (item.name.toLowerCase().includes('cash') || item.name.toLowerCase().includes('checking') || item.name === cashItemName)
    );

    if (existingBsIndex >= 0) {
      const existing = balanceSheet[existingBsIndex];
      updateBalanceSheetItem({
        ...existing,
        value: targetAmt,
        date: now.toISOString().split('T')[0]
      });
    } else {
      addBalanceSheetItem({
        id: generateUUID(),
        name: cashItemName,
        type: 'Asset',
        value: targetAmt,
        startDate: now.toISOString().split('T')[0],
        date: now.toISOString().split('T')[0]
      });
    }

    return { entry: newEntry, previousAmount };
  };

  const updateCashLedgerEntry = (id: string, newAmount: number, newNote?: string) => {
    const now = new Date();
    const updated = cashLedgerHistory.map(e => {
      if (e.id === id) {
        return {
          ...e,
          amount: newAmount,
          note: newNote !== undefined ? newNote : e.note,
          timestamp: now.toISOString(),
          timestampFormatted: now.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        };
      }
      return e;
    });
    saveCashLedgerHistory(updated);

    // If updating current month's entry, update availableCash as well
    const targetEntry = updated.find(e => e.id === id);
    if (targetEntry && targetEntry.monthIndex === now.getMonth() && targetEntry.year === now.getFullYear()) {
      setAvailableCash(newAmount);

      // Also keep Account Ledger (balanceSheet) cash item in sync
      const cashItemName = 'Liquid Cash Reserves';
      const existingBsIndex = balanceSheet.findIndex(item => 
        item.type === 'Asset' && (item.name.toLowerCase().includes('cash') || item.name.toLowerCase().includes('checking') || item.name === cashItemName)
      );
      if (existingBsIndex >= 0) {
        const existing = balanceSheet[existingBsIndex];
        updateBalanceSheetItem({
          ...existing,
          value: newAmount,
          date: now.toISOString().split('T')[0]
        });
      }
    }
  };

  const deleteCashLedgerEntry = (id: string) => {
    const updated = cashLedgerHistory.filter(e => e.id !== id);
    saveCashLedgerHistory(updated);
  };

  // Load credit cards and ledger from database on initial boot or user change
  useEffect(() => {
    const activeUserId = user?.id || 'default';
    fetch(`/api/credit-cards?userId=${encodeURIComponent(activeUserId)}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success) {
          if (Array.isArray(data.creditCards)) {
            setCreditCards(data.creditCards);
            try { 
              localStorage.setItem('fi_credit_cards', JSON.stringify(data.creditCards));
              if (user?.id) localStorage.setItem(`fi_credit_cards_${user.id}`, JSON.stringify(data.creditCards));
            } catch (_) {}
          }
          if (Array.isArray(data.creditCardLedger) && data.creditCardLedger.length > 0) {
            setCreditCardLedger(data.creditCardLedger);
            try { 
              localStorage.setItem('fi_credit_card_ledger', JSON.stringify(data.creditCardLedger));
              if (user?.id) localStorage.setItem(`fi_credit_card_ledger_${user.id}`, JSON.stringify(data.creditCardLedger));
            } catch (_) {}
          }
        }
      })
      .catch(e => console.warn('[CreditCard DB] Initial load failed:', e));
  }, [user?.id]);

  // Get last historical cash for a specific month (used for the balance formula)
  const getLastHistoricalCashForMonth = (monthIndex: number, year: number = selectedYear): number | null => {
    const matches = cashLedgerHistory
      .filter(e => e.monthIndex === monthIndex && e.year === year)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    if (matches.length > 0) {
      return matches[0].amount;
    }
    return null;
  };

  useEffect(() => {
    if (!user || !supabase) return;
    
    const fetchAllData = async () => {
      try {
        const [
          { data: prof },
          { data: exp },
          { data: budg },
          { data: inv },
          { data: bs },
          { data: cfe },
          { data: invCats },
          { data: hist }
        ] = await Promise.all([
          supabase!.from('profiles').select('*').eq('id', user.id).maybeSingle(),
          supabase!.from('expenses').select('*').eq('user_id', user.id),
          supabase!.from('budgets').select('*').eq('user_id', user.id),
          supabase!.from('investments').select('*').eq('user_id', user.id),
          supabase!.from('balance_sheet').select('*').eq('user_id', user.id),
          supabase!.from('cash_flow_events').select('*').eq('user_id', user.id),
          supabase!.from('investment_categories').select('*').eq('user_id', user.id),
          supabase!.from('investment_category_history').select('*').eq('user_id', user.id).order('date', { ascending: true })
        ]);

        // 1. Process Profile & Credit Cards database sync
        const ccRowsFromBs = (bs || []).filter(b => b.name && b.name.startsWith('[Credit Card]'));
        const ccFromBs: CreditCard[] = ccRowsFromBs.map(b => {
          const rawName = b.name.replace('[Credit Card]', '').trim();
          const metaList = prof?.jan_baseline?.creditCards || [];
          const meta = metaList.find((c: any) => c.id === b.id);
          return {
            id: b.id,
            name: rawName,
            issuer: meta?.issuer || (b.end_date && b.end_date !== 'PAID' && b.end_date !== 'UNPAID' ? b.end_date : 'Card'),
            last4: meta?.last4 || '',
            amountToPay: b.value ?? 0,
            minimumPayment: b.monthly_payment || 0,
            deadline: b.date || b.start_date || new Date().toISOString().split('T')[0],
            creditLimit: b.duration_months || meta?.creditLimit || 0,
            apr: b.remaining_months || meta?.apr || 0,
            isPaid: b.end_date === 'PAID' || meta?.isPaid === true,
            notes: meta?.notes || ''
          };
        });

        // 1. Process Profile & Credit Cards database sync
        let resolvedCards: CreditCard[] | null = null;
        try {
          const ccRes = await fetch(`/api/credit-cards?userId=${encodeURIComponent(user.id)}`);
          const serverData = await ccRes.json();
          if (serverData?.success && Array.isArray(serverData.creditCards) && serverData.creditCards.length > 0) {
            resolvedCards = serverData.creditCards;
            if (Array.isArray(serverData.creditCardLedger) && serverData.creditCardLedger.length > 0) {
              setCreditCardLedger(serverData.creditCardLedger);
              try {
                localStorage.setItem(`fi_credit_card_ledger_${user.id}`, JSON.stringify(serverData.creditCardLedger));
                localStorage.setItem('fi_credit_card_ledger', JSON.stringify(serverData.creditCardLedger));
              } catch (_) {}
            }
          }
        } catch (_) {}

        if (!resolvedCards) {
          if (Array.isArray(prof?.jan_baseline?.creditCards) && prof.jan_baseline.creditCards.length > 0) {
            resolvedCards = prof.jan_baseline.creditCards;
          } else if (ccFromBs.length > 0) {
            resolvedCards = ccFromBs;
          }
        }

        if (resolvedCards !== null) {
          setCreditCards(resolvedCards);
          try {
            localStorage.setItem(`fi_credit_cards_${user.id}`, JSON.stringify(resolvedCards));
            localStorage.setItem('fi_credit_cards_active', JSON.stringify(resolvedCards));
            localStorage.setItem('fi_credit_cards', JSON.stringify(resolvedCards));
          } catch (_) {}
          // Sync server backend DB for this user
          fetch('/api/credit-cards/sync', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'x-user-id': user.id
            },
            body: JSON.stringify({ userId: user.id, creditCards: resolvedCards })
          }).catch(() => {});
        }

        if (prof) {
          if (prof.currency) setCurrency(prof.currency as Currency);
          if (prof.exchange_rates) setExchangeRates(prof.exchange_rates);
          if (prof.jan_baseline) {
            setJanBaseline(prof.jan_baseline);
            if (Array.isArray(prof.jan_baseline.creditCardLedger) && prof.jan_baseline.creditCardLedger.length > 0) {
              setCreditCardLedger(prof.jan_baseline.creditCardLedger);
              try { 
                localStorage.setItem(`fi_credit_card_ledger_${user.id}`, JSON.stringify(prof.jan_baseline.creditCardLedger));
                localStorage.setItem('fi_credit_card_ledger', JSON.stringify(prof.jan_baseline.creditCardLedger)); 
              } catch (_) {}
            }
          }
        }
        
        setExpenses(exp || []);
        setBudgets(budg?.map(b => ({ id: b.id, category: b.category, budget: b.budget, icon: b.icon })) || []);
        
        const passiveMeta = (() => {
          try {
            return JSON.parse(localStorage.getItem('investments_passive_meta') || '{}');
          } catch {
            return {};
          }
        })();

        setInvestments(inv?.map(i => {
          const meta = passiveMeta[i.id] || passiveMeta[i.name] || {};
          let annual = i.annual_dividend !== undefined ? i.annual_dividend : meta.annualDividend;
          let yld = i.dividend_yield !== undefined ? i.dividend_yield : meta.dividendYield;
          
          // Sensible default heuristic for demo assets if not yet defined
          if (annual === undefined && yld === undefined) {
            const lowName = (i.name || '').toLowerCase();
            const lowCat = (i.category || '').toLowerCase();
            if (lowCat.includes('real estate') || lowName.includes('reit') || lowName.includes('property')) {
              yld = 6.2;
            } else if (lowName.includes('dividend') || lowName.includes('schd') || lowName.includes('vym')) {
              yld = 4.4;
            } else if (lowCat.includes('bond') || lowCat.includes('fixed') || lowName.includes('treasury')) {
              yld = 4.6;
            } else if (lowCat.includes('cash') || lowName.includes('t-bill')) {
              yld = 4.5;
            } else if (lowCat.includes('stock')) {
              yld = 1.8;
            }
            if (yld) annual = (i.invested_amount || 0) * (yld / 100);
          }

          return {
            id: i.id,
            name: i.name,
            category: i.category,
            investedAmount: i.invested_amount,
            currentValue: i.current_value,
            date: i.date,
            annualDividend: annual,
            dividendYield: yld,
            payoutFrequency: meta.payoutFrequency || (i.payout_frequency as any) || 'Quarterly'
          };
        }) || []);

        const categoriesWithHistory = (invCats || []).map(c => {
          const catHistory: HistoryPoint[] = (hist || [])
            .filter(h => h.category_id === c.id)
            .map(h => ({ date: h.date, value: h.value }));
          const latestVal = catHistory.length > 0 ? catHistory[catHistory.length - 1].value : 0;
          return {
            id: c.id,
            name: c.name,
            objective: c.objective,
            currentValue: latestVal,
            icon: c.icon,
            history: catHistory
          };
        });
        setInvestmentCategories(categoriesWithHistory);

        // Account Ledger items: exclude credit cards so they don't pollute regular ledger
        setBalanceSheet((bs || [])
          .filter(b => !b.name || !b.name.startsWith('[Credit Card]'))
          .map(b => ({
            id: b.id,
            type: b.type,
            name: b.name,
            value: b.value,
            date: b.date || b.start_date || new Date().toISOString().split('T')[0],
            isLoan: b.is_loan,
            monthlyPayment: b.monthly_payment,
            durationMonths: b.duration_months,
            remainingMonths: b.remaining_months,
            startDate: b.start_date || b.date || new Date().toISOString().split('T')[0],
            endDate: b.end_date
          }))
        );

        setCashFlowEvents(cfe?.map(c => ({
          id: c.id,
          description: c.description,
          amount: c.amount,
          type: c.type,
          frequency: c.frequency,
          startDate: c.start_date || c.date || new Date().toISOString().split('T')[0],
          endDate: c.end_date
        })) || []);
      } catch (err: any) {
        console.error("FinanceContext: Error fetching data", err?.message || err);
      }
    };
    fetchAllData();
  }, [user]);

  const convertToDisplay = (value: number) => value * (exchangeRates[currency] || 1);
  const convertFromInput = (value: number) => value / (exchangeRates[currency] || 1);
  
  const addInvestmentCategory = async (cat: InvestmentCategory): Promise<{ success: boolean; error?: string }> => {
    if (!user || !supabase) return { success: false, error: 'Not authenticated' };
    if (investmentCategories.some(c => c.name.toLowerCase() === cat.name.toLowerCase())) {
      return { success: false, error: `A category named "${cat.name}" already exists.` };
    }
    const insertId = isValidUUID(cat.id) ? cat.id : generateUUID();
    const { error } = await supabase.from('investment_categories').insert({
      id: insertId,
      user_id: user.id,
      name: cat.name,
      objective: cat.objective,
      icon: cat.icon
    });
    if (!error) {
      setInvestmentCategories(prev => [...prev, { ...cat, id: insertId, history: cat.history || [] }]);
      return { success: true };
    }
    return { success: false, error: error.message };
  };

  const updateInvestmentCategory = async (cat: InvestmentCategory) => {
    if (!user || !supabase) return;
    const oldCat = investmentCategories.find(c => c.id === cat.id);
    const nameChanged = oldCat && oldCat.name !== cat.name;
    const { error } = await supabase.from('investment_categories').update({
      name: cat.name,
      objective: cat.objective,
      icon: cat.icon
    }).eq('id', cat.id).eq('user_id', user.id);
    if (!error) {
      if (nameChanged) {
        await supabase.from('investments').update({ category: cat.name }).eq('category', oldCat.name).eq('user_id', user.id);
        setInvestments(prev => prev.map(inv => inv.category === oldCat.name ? { ...inv, category: cat.name } : inv));
      }
      setInvestmentCategories(prev => prev.map(c => c.id === cat.id ? cat : c));
    } else {
      console.error("Error updating investment category:", error.message);
    }
  };

  const updateCategoryValue = async (id: string, value: number, date: string) => {
    if (!user || !supabase) return;
    const { error } = await supabase.from('investment_category_history').upsert({
      category_id: id,
      user_id: user.id,
      value: value,
      date: date
    }, { onConflict: 'category_id,date' });
    
    if (error) {
      console.error("Error updating category value:", error.message);
      return;
    }

    setInvestmentCategories(prev => prev.map(c => {
      if (c.id === id) {
        let newHistory = [...c.history];
        const existingIdx = newHistory.findIndex(h => h.date === date);
        if (existingIdx >= 0) {
          newHistory[existingIdx] = { date, value };
        } else {
          newHistory.push({ date, value });
          newHistory.sort((a, b) => a.date.localeCompare(b.date));
        }
        const latestPoint = newHistory[newHistory.length - 1];
        return { ...c, currentValue: latestPoint?.value || 0, history: newHistory };
      }
      return c;
    }));
  };

  const deleteCategoryHistoryPoint = async (categoryId: string, date: string) => {
    if (!user || !supabase) return;
    const { error } = await supabase.from('investment_category_history').delete().eq('category_id', categoryId).eq('date', date).eq('user_id', user.id);
    if (error) {
      console.error("Error deleting category history point:", error.message);
      return;
    }
    setInvestmentCategories(prev => prev.map(c => {
      if (c.id === categoryId) {
        const newHistory = c.history.filter(h => h.date !== date);
        const latestPoint = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
        return { ...c, history: newHistory, currentValue: latestPoint ? latestPoint.value : 0 };
      }
      return c;
    }));
  };

  const deleteInvestmentCategory = async (id: string) => {
    if (!user || !supabase) return;
    const catToDelete = investmentCategories.find(c => c.id === id);
    if (!catToDelete) return;
    try {
      await supabase.from('investment_category_history').delete().eq('category_id', id).eq('user_id', user.id);
      await supabase.from('investments').delete().eq('category', catToDelete.name).eq('user_id', user.id);
      const { error } = await supabase.from('investment_categories').delete().eq('id', id).eq('user_id', user.id);
      if (!error) {
        setInvestmentCategories(prev => prev.filter(c => c.id !== id));
        setInvestments(prev => prev.filter(inv => inv.category !== catToDelete.name));
      } else {
        console.error("Delete investment category failed:", error.message);
      }
    } catch (err: any) {
      console.error("Delete failed unexpected", err?.message || err);
    }
  };

  const addInvestment = async (i: Investment) => {
    const insertId = isValidUUID(i.id) ? i.id : generateUUID();
    
    // Persist passive income metadata in localStorage
    try {
      const meta = JSON.parse(localStorage.getItem('investments_passive_meta') || '{}');
      meta[insertId] = {
        annualDividend: i.annualDividend,
        dividendYield: i.dividendYield,
        payoutFrequency: i.payoutFrequency
      };
      meta[i.name] = meta[insertId];
      localStorage.setItem('investments_passive_meta', JSON.stringify(meta));
    } catch (e) {
      console.warn("Could not save passive meta", e);
    }

    if (user && supabase) {
      const { error } = await supabase.from('investments').insert({
        id: insertId,
        user_id: user.id,
        name: i.name,
        category: i.category,
        invested_amount: i.investedAmount,
        current_value: i.investedAmount, 
        date: i.date
      });
      if (error) {
        console.warn("Wealth: Adding investment to DB had note:", error.message);
      }
    }

    setInvestments(prev => [...prev, { ...i, id: insertId, currentValue: i.investedAmount }]);
  };

  const updateInvestment = async (updated: Investment) => {
    // Persist passive income metadata in localStorage
    try {
      const meta = JSON.parse(localStorage.getItem('investments_passive_meta') || '{}');
      meta[updated.id] = {
        annualDividend: updated.annualDividend,
        dividendYield: updated.dividendYield,
        payoutFrequency: updated.payoutFrequency
      };
      meta[updated.name] = meta[updated.id];
      localStorage.setItem('investments_passive_meta', JSON.stringify(meta));
    } catch (e) {
      console.warn("Could not save passive meta", e);
    }

    if (user && supabase) {
      const { error } = await supabase.from('investments').update({
        name: updated.name,
        category: updated.category,
        invested_amount: updated.investedAmount,
        date: updated.date
      }).eq('id', updated.id).eq('user_id', user.id);
      if (error) {
        console.warn("Wealth: Updating investment in DB note:", error.message);
      }
    }

    setInvestments(prev => prev.map(inv => inv.id === updated.id ? updated : inv));
  };

  const deleteInvestment = async (id: string) => {
    if (!user || !supabase) return;
    if (!id) {
        console.warn("Wealth: Delete attempt blocked - No persistent ID found for this record.");
        return;
    }
    
    // Use explicit .eq filters for reliable deletion in Supabase JS v2
    const { error } = await supabase
      .from('investments')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    
    if (!error) {
      setInvestments(prev => prev.filter(i => String(i.id) !== String(id)));
    } else {
      console.error("Wealth: Delete failed in Supabase. Check RLS or Primary Key mismatch.", error.message);
    }
  };

  const handleSetJanBaseline = async (baseline: JanuaryBaseline) => {
    if (!user || !supabase) return;
    try {
      const { data: prof } = await supabase.from('profiles').select('jan_baseline').eq('id', user.id).maybeSingle();
      const currentBase = prof?.jan_baseline || {};
      const merged = {
        ...currentBase,
        ...baseline,
        creditCards: currentBase.creditCards || creditCards,
        creditCardLedger: currentBase.creditCardLedger || creditCardLedger
      };
      const { error } = await supabase.from('profiles').upsert({ id: user.id, jan_baseline: merged });
      if (error) throw error;
      setJanBaseline(baseline);
    } catch (dbErr: any) {
      console.error("Failed to save baseline:", dbErr.message || dbErr);
    }
  };

  const updateExchangeRate = async (curr: Currency, rate: number) => {
    if (curr === Currency.USD || !user || !supabase) return;
    const newRates = { ...exchangeRates, [curr]: rate };
    setExchangeRates(newRates);
    await supabase.from('profiles').update({ exchange_rates: newRates }).eq('id', user.id);
  };

  const handleSetCurrency = async (c: Currency) => {
    setCurrency(c);
    if (user && supabase) await supabase.from('profiles').update({ currency: c }).eq('id', user.id);
  };

  const addExpenses = async (newExpenses: Expense[]) => {
    if (!user || !supabase) return;
    const toInsert = newExpenses.map(e => ({
      id: isValidUUID(e.id) ? e.id : generateUUID(),
      user_id: user.id,
      date: e.date,
      description: e.description,
      amount: e.amount,
      category: e.category
    }));
    const { data, error } = await supabase.from('expenses').insert(toInsert).select();
    if (!error && data) {
      setExpenses(prev => [...prev, ...data.map(d => ({
        id: d.id, date: d.date, description: d.description, amount: d.amount, category: d.category, currency: d.currency || currency
      }))]);
    } else {
      console.error("Error adding expenses:", error?.message);
    }
  };

  const deleteExpense = async (id: string) => {
    if (!user || !supabase) return;
    const { error } = await supabase.from('expenses').delete().eq('id', id).eq('user_id', user.id);
    if (!error) {
      setExpenses(prev => prev.filter(e => e.id !== id));
    } else {
      console.error("Error deleting expense:", error.message);
    }
  };

  const updateExpense = async (updated: Expense) => {
    if (!user || !supabase) return;
    const { id, ...data } = updated;
    const { error } = await supabase.from('expenses').update({
      date: data.date, description: data.description, amount: data.amount, category: data.category
    }).eq('id', id).eq('user_id', user.id);
    if (!error) {
      setExpenses(prev => prev.map(e => e.id === updated.id ? updated : e));
    } else {
      console.error("Error updating expense:", error.message);
    }
  };

  const updateBudget = async (category: string, amount: number, icon?: string, oldName?: string) => {
    if (!user || !supabase) return;
    const trimmedCategory = category.trim();

    if (isNaN(amount) || !isFinite(amount)) {
      console.error("Wealth: Invalid budget amount submitted.");
      return;
    }

    const isRename = !!(oldName && oldName.trim() !== trimmedCategory);

    if (amount <= 0 && !isRename) {
      const { error } = await supabase.from('budgets').delete().eq('user_id', user.id).eq('category', trimmedCategory);
      if (!error) {
        setBudgets(prev => prev.filter(b => b.category !== trimmedCategory));
      } else {
        console.error("Wealth: Error deleting budget:", error.message);
      }
      return;
    }

    if (isRename) {
        await supabase.from('budgets').delete().eq('user_id', user.id).eq('category', oldName.trim());
    }

    const budgetPayload: any = { user_id: user.id, category: trimmedCategory, budget: amount };
    if (icon) budgetPayload.icon = icon;

    const { data: upsertedData, error } = await supabase.from('budgets').upsert(
      budgetPayload,
      { onConflict: 'user_id,category' }
    ).select().single();

    if (error) {
      if (error.message.includes('icon') || error.code === '42703') {
         const { data: retryData, error: retryError } = await supabase.from('budgets').upsert(
           { user_id: user.id, category: trimmedCategory, budget: amount },
           { onConflict: 'user_id,category' }
         ).select().single();
         
         if (retryError) {
           console.error("Wealth: Error updating budget (retry failed):", retryError.message);
           return;
         }
         updateLocalBudgetState(trimmedCategory, amount, icon, isRename ? oldName!.trim() : undefined, retryData);
      } else {
        console.error("Wealth: Error updating budget:", error.message);
        return;
      }
    } else {
       updateLocalBudgetState(trimmedCategory, amount, icon, isRename ? oldName!.trim() : undefined, upsertedData);
    }
  };

  const updateLocalBudgetState = (category: string, amount: number, icon?: string, oldName?: string, dbRow?: any) => {
    const isRename = !!oldName;

    if (isRename && user) {
       supabase!.from('expenses').update({ category }).eq('user_id', user.id).eq('category', oldName)
        .then(({ error }) => {
          if (error) console.error("Wealth: Error updating expenses category:", error.message);
        });
       setExpenses(prev => prev.map(e => e.category === oldName ? { ...e, category } : e));
    }

    setBudgets(prev => {
      const lookupName = isRename ? oldName : category;
      const existingIdx = prev.findIndex(b => b.category === lookupName);
      
      const newBudgetEntry: CategoryBudget = { 
        id: dbRow?.id || (existingIdx !== -1 ? prev[existingIdx].id : generateUUID()),
        category, 
        budget: amount, 
        icon: icon || (existingIdx !== -1 ? prev[existingIdx].icon : undefined) 
      };
      
      if (existingIdx !== -1) {
        const next = [...prev];
        next[existingIdx] = newBudgetEntry;
        return next;
      } else {
        return [...prev, newBudgetEntry];
      }
    });
  };

  const addCategory = async (category: string, budget: number, icon?: string) => updateBudget(category, budget, icon);

  const addBalanceSheetItem = async (item: BalanceSheetItem) => {
    if (!user || !supabase) return;
    const insertId = isValidUUID(item.id) ? item.id : generateUUID();
    const { data, error } = await supabase.from('balance_sheet').insert({
      id: insertId,
      user_id: user.id,
      type: item.type,
      name: item.name,
      value: item.value,
      is_loan: item.isLoan || false,
      monthly_payment: item.monthlyPayment || 0,
      duration_months: item.durationMonths,
      remaining_months: item.remainingMonths,
      date: item.date || item.startDate,
      start_date: item.startDate,
      end_date: item.endDate || null
    }).select().single();
    if (!error && data) {
      setBalanceSheet(prev => [...prev, {
        id: data.id, type: data.type, name: data.name, value: data.value, date: data.date,
        isLoan: data.is_loan, monthlyPayment: data.monthly_payment, durationMonths: data.duration_months,
        remainingMonths: data.remaining_months, startDate: data.start_date, endDate: data.end_date
      }]);
    } else {
      console.error("Error adding balance sheet item:", error?.message);
    }
  };

  const updateBalanceSheetItem = async (item: BalanceSheetItem) => {
    if (!user || !supabase) return;
    const { error } = await supabase.from('balance_sheet').update({
      name: item.name, value: item.value, monthly_payment: item.monthlyPayment,
      remaining_months: item.remainingMonths, duration_months: item.durationMonths,
      is_loan: item.isLoan, start_date: item.startDate, end_date: item.endDate || null
    }).eq('id', item.id).eq('user_id', user.id);
    if (!error) {
      setBalanceSheet(prev => prev.map(i => i.id === item.id ? item : i));
    } else {
      console.error("Error updating balance sheet item:", error.message);
    }
  };

  const deleteBalanceSheetItem = async (id: string) => {
    if (!user || !supabase) return;
    const { error } = await supabase.from('balance_sheet').delete().eq('id', id).eq('user_id', user.id);
    if (!error) {
      setBalanceSheet(prev => prev.filter(i => i.id !== id));
    } else {
      console.error("Error deleting balance sheet item:", error.message);
    }
  };

  const addCashFlow = (cf: CashFlowDetail) => setCashFlows(prev => [...prev, cf]);

  const saveCashFlowEvents = (events: CashFlowEvent[]) => {
    setCashFlowEvents(events);
    try {
      localStorage.setItem('fi_cash_flow_events', JSON.stringify(events));
    } catch (e) {
      console.error(e);
    }
  };

  const addCashFlowEvent = async (event: CashFlowEvent) => {
    const insertId = isValidUUID(event.id) ? event.id : generateUUID();
    const newEvent: CashFlowEvent = { ...event, id: insertId };
    const updated = [...cashFlowEvents, newEvent];
    saveCashFlowEvents(updated);

    if (user && supabase) {
      try {
        await supabase.from('cash_flow_events').insert({
          id: insertId, user_id: user.id, description: event.description, amount: event.amount,
          type: event.type, frequency: event.frequency, start_date: event.startDate, end_date: event.endDate || null
        });
      } catch (err: any) {
        console.error("Error adding cash flow event to supabase:", err?.message);
      }
    }
  };

  const updateCashFlowEvent = async (event: CashFlowEvent) => {
    const updated = cashFlowEvents.map(e => e.id === event.id ? event : e);
    saveCashFlowEvents(updated);

    if (user && supabase) {
      try {
        await supabase.from('cash_flow_events').update({
          description: event.description, amount: event.amount, type: event.type,
          frequency: event.frequency, start_date: event.startDate, end_date: event.endDate || null
        }).eq('id', event.id).eq('user_id', user.id);
      } catch (err: any) {
        console.error("Error updating cash flow event in supabase:", err?.message);
      }
    }
  };

  const deleteCashFlowEvent = async (id: string) => {
    const updated = cashFlowEvents.filter(e => e.id !== id);
    saveCashFlowEvents(updated);

    if (user && supabase) {
      try {
        await supabase.from('cash_flow_events').delete().eq('id', id).eq('user_id', user.id);
      } catch (err: any) {
        console.error("Error deleting cash flow event in supabase:", err?.message);
      }
    }
  };

  // Passive Income Calculations
  const passiveIncomeStats = useMemo(() => {
    let totalAnnualPassive = 0;
    let assetsCount = 0;

    investments.forEach(inv => {
      let annual = 0;
      if (inv.annualDividend !== undefined && inv.annualDividend > 0) {
        annual = inv.annualDividend;
      } else if (inv.dividendYield !== undefined && inv.dividendYield > 0) {
        annual = inv.investedAmount * (inv.dividendYield / 100);
      }
      if (annual > 0) {
        totalAnnualPassive += annual;
        assetsCount++;
      }
    });

    const totalMonthlyPassive = totalAnnualPassive / 12;
    const totalInvested = investments.reduce((s, i) => s + (i.investedAmount || 0), 0);
    const portfolioYield = totalInvested > 0 ? (totalAnnualPassive / totalInvested) * 100 : 0;
    const progressToGoal = passiveIncomeGoal > 0 ? Math.min((totalMonthlyPassive / passiveIncomeGoal) * 100, 100) : 0;

    return {
      totalAnnualPassive,
      totalMonthlyPassive,
      portfolioYield,
      progressToGoal,
      assetsCount,
      passiveIncomeGoal
    };
  }, [investments, passiveIncomeGoal]);

  // Financial Freedom Calculations (Passive Income + 4% Safe Withdrawal Rule)
  const financialFreedomStats = useMemo<FinancialFreedomStats>(() => {
    // 1. Lifestyle monthly expenses: defaults to budgeted monthly expenses
    const budgetDefaultMonthly = budgets.reduce((sum, b) => sum + (b.budget || 0), 0);
    const effectiveBudget = budgetDefaultMonthly > 0 ? budgetDefaultMonthly : 3500;
    const isCustomLifestyle = lifestyleMonthlyExpenses !== null;
    const lifestyleMonthly = isCustomLifestyle ? (lifestyleMonthlyExpenses || 0) : effectiveBudget;
    const lifestyleAnnual = lifestyleMonthly * 12;

    // 2. Passive Income capacity
    const totalAnnualPassive = passiveIncomeStats.totalAnnualPassive;
    const totalMonthlyPassive = passiveIncomeStats.totalMonthlyPassive;

    // 3. Portfolio Invested Capital & 4% Withdrawal Capacity
    const categoryVal = investmentCategories.reduce((acc, curr) => acc + (curr.currentValue || 0), 0);
    const deployedVal = investments.reduce((s, i) => s + (i.investedAmount || 0), 0);
    const portfolioValue = categoryVal > 0 ? categoryVal : deployedVal;

    // Standard 4% annual withdrawal rule
    const annualWithdrawal4Pct = portfolioValue * 0.04;
    const monthlyWithdrawal4Pct = annualWithdrawal4Pct / 12;

    // 4. Combined Financial Freedom Generating Capacity
    const totalAnnualFreedomIncome = totalAnnualPassive + annualWithdrawal4Pct;
    const totalMonthlyFreedomIncome = totalMonthlyPassive + monthlyWithdrawal4Pct;

    // 5. Freedom Coverage Percentage (% of lifestyle covered by Passive + 4% Rule)
    const rawFreedomPercentage = lifestyleAnnual > 0 ? (totalAnnualFreedomIncome / lifestyleAnnual) * 100 : 0;
    const freedomPercentage = Math.min(100, Math.max(0, rawFreedomPercentage));

    // 6. Shortfall & Capital Needed to Complete Freedom
    const annualShortfall = Math.max(0, lifestyleAnnual - totalAnnualFreedomIncome);
    const monthlyShortfall = Math.max(0, lifestyleMonthly - totalMonthlyFreedomIncome);

    // Each dollar invested earns via the standard 4% withdrawal rule (25x multiplier)
    const capitalNeededToInvest = annualShortfall > 0 ? annualShortfall / 0.04 : 0;

    // Target capital required to fund lifestyle considering current passive income
    const targetNetAnnualFrom4Pct = Math.max(0, lifestyleAnnual - totalAnnualPassive);
    const totalFreedomTargetCapital = targetNetAnnualFrom4Pct / 0.04;

    // Total effective wealth: portfolio capital + capitalized value of perpetual passive dividends at 4%
    const effectiveWealth = portfolioValue + (totalAnnualPassive / 0.04);
    const fullLifestyleCapital = lifestyleAnnual * 25;
    const isFreedomAchieved = totalAnnualFreedomIncome >= lifestyleAnnual && lifestyleAnnual > 0;

    return {
      lifestyleMonthly,
      lifestyleAnnual,
      isCustomLifestyle,
      budgetDefaultMonthly,
      totalAnnualPassive,
      totalMonthlyPassive,
      portfolioValue,
      annualWithdrawal4Pct,
      monthlyWithdrawal4Pct,
      totalAnnualFreedomIncome,
      totalMonthlyFreedomIncome,
      freedomPercentage,
      rawFreedomPercentage,
      annualShortfall,
      monthlyShortfall,
      capitalNeededToInvest,
      totalFreedomTargetCapital,
      effectiveWealth,
      fullLifestyleCapital,
      isFreedomAchieved
    };
  }, [budgets, lifestyleMonthlyExpenses, passiveIncomeStats, investmentCategories, investments]);

  return (
    <FinanceContext.Provider value={{
      currency, setCurrency: handleSetCurrency, exchangeRates, updateExchangeRate, convertToDisplay, convertFromInput,
      expenses, addExpenses, deleteExpense, updateExpense,
      budgets, updateBudget, addCategory,
      investments, addInvestment, updateInvestment, deleteInvestment,
      investmentCategories, addInvestmentCategory, updateInvestmentCategory, updateCategoryValue, deleteInvestmentCategory,
      deleteCategoryHistoryPoint,
      balanceSheet, addBalanceSheetItem, updateBalanceSheetItem, deleteBalanceSheetItem,
      cashFlows, addCashFlow,
      cashFlowEvents, addCashFlowEvent, updateCashFlowEvent, deleteCashFlowEvent,
      janBaseline, setJanBaseline: handleSetJanBaseline,
      isDarkMode, setIsDarkMode,
      passiveIncomeGoal,
      setPassiveIncomeGoal,
      passiveIncomeStats,
      lifestyleMonthlyExpenses,
      setLifestyleMonthlyExpenses,
      financialFreedomStats,
      creditCards,
      addCreditCard,
      updateCreditCard,
      deleteCreditCard,
      toggleCreditCardPaid,
      creditCardLedger,
      recordCreditCardPayment,
      deleteCreditCardPaymentRecord,
      availableCash,
      setAvailableCash,
      cashLedgerHistory,
      syncCashLedger,
      updateCashLedgerEntry,
      deleteCashLedgerEntry,
      getLastHistoricalCashForMonth,
      selectedYear,
      setSelectedYear,
      availableYears
    }}>
      {children}
    </FinanceContext.Provider>
  );
};

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) throw new Error("useFinance must be used within FinanceProvider");
  return context;
};
